import React, { useState, useEffect } from 'react';
import { Campaign } from '../../progress/types';
import { CampaignService } from '../../progress/services/CampaignService';
import CampaignEditor from './CampaignEditor';

const CampaignsTab: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const campaignList = await CampaignService.getAllCampaigns();
      setCampaigns(campaignList);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setMessage({ type: 'error', text: 'Failed to load campaigns' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedCampaign(null);
    setShowEditor(true);
  };

  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowEditor(true);
  };

  const handleEditorSave = async (campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (selectedCampaign) {
        // Update existing campaign
        const success = await CampaignService.updateCampaign(selectedCampaign.id!, campaignData);
        if (success) {
          setMessage({ type: 'success', text: 'Campaign updated successfully' });
          await loadCampaigns();
        } else {
          setMessage({ type: 'error', text: 'Failed to update campaign' });
        }
      } else {
        // Create new campaign
        const campaignId = await CampaignService.createCampaign(campaignData);
        if (campaignId) {
          setMessage({ type: 'success', text: 'Campaign created successfully' });
          await loadCampaigns();
        } else {
          setMessage({ type: 'error', text: 'Failed to create campaign' });
        }
      }
      setShowEditor(false);
      setSelectedCampaign(null);
    } catch (error) {
      console.error('Error saving campaign:', error);
      setMessage({ type: 'error', text: 'Failed to save campaign' });
    }
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setSelectedCampaign(null);
  };

  const handleToggleStatus = async (campaignId: string, currentStatus: boolean) => {
    try {
      const success = await CampaignService.toggleCampaignStatus(campaignId, !currentStatus);
      if (success) {
        setMessage({ 
          type: 'success', 
          text: `Campaign ${!currentStatus ? 'activated' : 'deactivated'} successfully` 
        });
        await loadCampaigns();
      } else {
        setMessage({ type: 'error', text: 'Failed to update campaign status' });
      }
    } catch (error) {
      console.error('Error toggling campaign status:', error);
      setMessage({ type: 'error', text: 'Failed to update campaign status' });
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!await window.appConfirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return;
    }

    try {
      const success = await CampaignService.deleteCampaign(campaignId);
      if (success) {
        setMessage({ type: 'success', text: 'Campaign deleted successfully' });
        await loadCampaigns();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete campaign' });
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setMessage({ type: 'error', text: 'Failed to delete campaign' });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (showEditor) {
    return (
      <CampaignEditor
        campaign={selectedCampaign}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
      />
    );
  }

  return (
    <div className="campaigns-tab">
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, color: '#2C3E50' }}>Campaign Management</h2>
          <button
            onClick={handleCreateNew}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #4FC3F7 0%, #45B7D1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            ➕ Create New Campaign
          </button>
        </div>

        {message && (
          <div
            style={{
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              background: message.type === 'success' 
                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(104, 159, 56, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)',
              border: `1px solid ${message.type === 'success' ? '#4CAF50' : '#F44336'}`,
              color: message.type === 'success' ? '#2E7D32' : '#C62828'
            }}
          >
            {message.text}
            <button
              onClick={() => setMessage(null)}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                color: 'inherit'
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p>Loading campaigns...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            background: 'linear-gradient(135deg, rgba(79, 195, 247, 0.1) 0%, rgba(162, 89, 230, 0.1) 100%)',
            borderRadius: '1rem',
            border: '2px dashed rgba(79, 195, 247, 0.3)'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
          <h3 style={{ color: '#2C3E50', marginBottom: '0.5rem' }}>No Campaigns Yet</h3>
          <p style={{ color: '#546E7A', marginBottom: '1.5rem' }}>
            Create your first campaign to start collecting student feedback beyond Block A questions.
          </p>
          <button
            onClick={handleCreateNew}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #4FC3F7 0%, #45B7D1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Create First Campaign
          </button>
        </div>
      ) : (
        <div className="campaigns-list">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              style={{
                background: '#fff',
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: '1rem',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, color: '#2C3E50', fontSize: '1.3rem' }}>
                      {campaign.name}
                    </h3>
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        background: campaign.active 
                          ? 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)'
                          : 'linear-gradient(135deg, #9E9E9E 0%, #757575 100%)',
                        color: '#fff'
                      }}
                    >
                      {campaign.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ color: '#546E7A', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    <strong>Duration:</strong> {formatDate(campaign.dateRange.start)} → {formatDate(campaign.dateRange.end)}
                  </div>
                  <div style={{ color: '#546E7A', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    <strong>Squads:</strong> {campaign.squadIds.length} squad{campaign.squadIds.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ color: '#546E7A', fontSize: '0.9rem' }}>
                    <strong>Questions:</strong> {campaign.questions.length} question{campaign.questions.length === 1 ? '' : 's'}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(campaign)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  
                  <button
                    onClick={() => handleToggleStatus(campaign.id!, campaign.active)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: campaign.active 
                        ? 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
                        : 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {campaign.active ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                  
                  <button
                    onClick={() => handleDelete(campaign.id!)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>

              {/* Questions Preview */}
              {campaign.questions.length > 0 && (
                <div
                  style={{
                    background: 'rgba(79, 195, 247, 0.1)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginTop: '1rem'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2C3E50', fontSize: '1rem' }}>
                    Questions Preview:
                  </h4>
                  {campaign.questions.slice(0, 2).map((q, index) => (
                    <div key={index} style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#546E7A' }}>
                      <strong>{index + 1}.</strong> {q.prompt} 
                      <span style={{ 
                        color: q.type === 'multiple_choice' ? '#4CAF50' : '#FF9800',
                        fontWeight: '600',
                        marginLeft: '0.5rem'
                      }}>
                        ({q.type === 'multiple_choice' ? 'Multiple Choice' : 'Text'})
                      </span>
                    </div>
                  ))}
                  {campaign.questions.length > 2 && (
                    <div style={{ fontSize: '0.9rem', color: '#9E9E9E', fontStyle: 'italic' }}>
                      ... and {campaign.questions.length - 2} more question{campaign.questions.length - 2 === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignsTab;